    /**
 * Text Size Classes Configuration
 * 
 * Provides consistent text sizing across the application with accessibility support.
 * Includes three size presets (small, medium, large) for different user preferences.
 */

import { TextSizeOverrides } from "../types/text-size";

// Type definitions for text size classes
export type TextSize = 'small' | 'medium' | 'large';

// Represents a CSS class name for text size
export type TextSizeClass = string;

export type TextSizeConfiguration = Record<TextSize, TextSizeClassMap>;

export interface TextSizeClassMap {
  h1: string;
  h2: string;
  h3: string;
  p: string;
  label: string;
  input: string;
  button: string;
  title: string;
  description: string;
  difficulty: string;
  value: string;
  script: string;
  message: string;
  post: string;
  headline: string;
  feature: string;
  body: string;
  result: string;
  feedback: string;
  summaryTitle: string;
  summaryItem: string;
  redFlagText: string;
  statValue: string;
  statLabel: string;
  scenarioTitle: string;
  scenarioStatus: string;
  small: string; // <-- Added
}

export const textSizeClasses: Record<TextSize, TextSizeClassMap> = {
  small: {
    h1: 'text-3xl',
    h2: 'text-3xl',
    h3: 'text-xl',
    p: 'text-base',
    label: 'text-base',
    input: 'text-base',
    button: 'text-lg',
    title: 'text-xl',
    description: 'text-sm',
    difficulty: 'text-xs',
    value: 'text-base',
    script: 'text-base',
    message: 'text-base',
    post: 'text-base',
    headline: 'text-base',
    feature: 'text-base',
    body: 'text-base',
    result: 'text-base',
    feedback: 'text-sm',
    summaryTitle: 'text-xl',
    summaryItem: 'text-base',
    redFlagText: 'text-sm',
    statValue: 'text-3xl',
    statLabel: 'text-sm',
    scenarioTitle: 'text-base',
    scenarioStatus: 'text-sm',
    small: 'text-sm', // <-- Added
  },
  medium: {
    h1: 'text-4xl',
    h2: 'text-4xl',
    h3: 'text-2xl',
    p: 'text-lg',
    label: 'text-lg',
    input: 'text-lg',
    button: 'text-xl',
    title: 'text-2xl',
    description: 'text-base',
    difficulty: 'text-sm',
    value: 'text-lg',
    script: 'text-lg',
    message: 'text-lg',
    post: 'text-lg',
    headline: 'text-lg',
    feature: 'text-lg',
    body: 'text-lg',
    result: 'text-lg',
    feedback: 'text-base',
    summaryTitle: 'text-2xl',
    summaryItem: 'text-lg',
    redFlagText: 'text-base',
    statValue: 'text-4xl',
    statLabel: 'text-base',
    scenarioTitle: 'text-lg',
    scenarioStatus: 'text-base',
    small: 'text-sm', // <-- Added
  },
  large: {
    h1: 'text-5xl',
    h2: 'text-5xl',
    h3: 'text-3xl',
    p: 'text-xl',
    label: 'text-xl',
    input: 'text-xl',
    button: 'text-2xl',
    title: 'text-3xl',
    description: 'text-lg',
    difficulty: 'text-base',
    value: 'text-xl',
    script: 'text-xl',
    message: 'text-xl',
    post: 'text-xl',
    headline: 'text-xl',
    feature: 'text-xl',
    body: 'text-xl',
    result: 'text-xl',
    feedback: 'text-lg',
    summaryTitle: 'text-3xl',
    summaryItem: 'text-xl',
    redFlagText: 'text-lg',
    statValue: 'text-5xl',
    statLabel: 'text-lg',
    scenarioTitle: 'text-xl',
    scenarioStatus: 'text-lg',
    small: 'text-sm', // <-- Added
  },
};

/**
 * Merges custom text size overrides with default text size classes
 * 
 * Allows components to customize specific text size classes while maintaining
 * the base structure. Useful for component-specific styling needs.
 * 
 * @param overrides - Custom text size class overrides
 * @returns Merged text size classes with custom overrides applied
 * 
 * @example
 * const customClasses = mergeTextSizeClasses({
 *   medium: { h1: 'text-5xl', button: 'text-xl' },
 *   large: { h1: 'text-6xl' }
 * });
 */
export const mergeTextSizeClasses = (overrides: TextSizeOverrides = {}): TextSizeConfiguration => ({
  small: { ...textSizeClasses.small, ...(overrides.small || {}) },
  medium: { ...textSizeClasses.medium, ...(overrides.medium || {}) },
  large: { ...textSizeClasses.large, ...(overrides.large || {}) },
});

/**
 * Safely get text size classes with error handling
 * @param textSize - The text size key
 * @param defaultSize - The default size to use
 * @returns Text size classes for the given size
 */
export const getSafeTextSizeClasses = (
  textSize: TextSize | string | undefined | null, 
  defaultSize: TextSize = 'medium'
): TextSizeClassMap => {
  if (textSize && textSize in textSizeClasses) {
    return textSizeClasses[textSize as TextSize];
  }
  if (textSize && !(textSize in textSizeClasses)) {
    console.warn(
      `Invalid textSize "${textSize}" provided. Using "${defaultSize}" instead.`
    );
  }
  return textSizeClasses[defaultSize] || textSizeClasses.medium;
};

/**
 * Backward compatibility utility functions for simulation detail components.
 * Prefer getSafeTextSizeClasses(textSize) directly, but these are kept for older code.
 */

/**
 * Gets heading class for simulation detail components
 * 
 * @param textSize - Text size preference ('small', 'medium', 'large')
 * @returns CSS class for h2 elements
 * @deprecated Use getSafeTextSizeClasses(textSize).h2 instead
 */
export const getSimulationDetailHeadingClass = (textSize: TextSize | string | undefined | null): TextSizeClass =>
  getSafeTextSizeClasses(textSize).h2;

/**
 * Gets description class for simulation detail components
 * 
 * @param textSize - Text size preference ('small', 'medium', 'large')
 * @returns CSS class for paragraph elements
 * @deprecated Use getSafeTextSizeClasses(textSize).p instead
 */
export const getSimulationDetailDescriptionClass = (textSize: TextSize | string | undefined | null): TextSizeClass =>
  getSafeTextSizeClasses(textSize).p;

/**
 * Gets subheading class for simulation detail components
 * 
 * @param textSize - Text size preference ('small', 'medium', 'large')
 * @returns CSS class for h3 elements
 * @deprecated Use getSafeTextSizeClasses(textSize).h3 instead
 */
export const getSimulationDetailSubheadingClass = (textSize: TextSize | string | undefined | null): TextSizeClass =>
  getSafeTextSizeClasses(textSize).h3;

// Export types
// (Types are already exported above; this block is redundant and removed)